function normalizeToSafeSameOriginPath(pathOrUrl, defaultPath = "/users/sign_in") {
  const safeDefaultPath =
    typeof defaultPath === "string" && defaultPath.trim().length > 0
      ? defaultPath
      : "/users/sign_in";

  if (typeof pathOrUrl !== "string" || pathOrUrl.trim().length === 0) {
    return safeDefaultPath;
  }

  try {
    const normalizedUrl = new URL(pathOrUrl, window.location.origin);
    if (normalizedUrl.origin === window.location.origin) {
      return `${normalizedUrl.pathname}${normalizedUrl.search}${normalizedUrl.hash}`;
    }
  } catch (_error) {
    // 無効なURLはフォールバックへ
  }

  return safeDefaultPath;
}

export function redirectToSafePath(redirectUrl, fallbackPath = "/users/sign_in") {
  const safeFallbackPath = normalizeToSafeSameOriginPath(fallbackPath);

  try {
    const safeRedirectUrl = new URL(redirectUrl, window.location.origin);

    if (safeRedirectUrl.origin === window.location.origin) {
      window.location.assign(
        `${safeRedirectUrl.pathname}${safeRedirectUrl.search}${safeRedirectUrl.hash}`
      );
      return true;
    }
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      const sanitizedRedirectUrl =
        typeof redirectUrl === "string"
          ? {
              length: redirectUrl.length,
              preview: redirectUrl.slice(0, 100).split("?")[0],
            }
          : { type: typeof redirectUrl };

      console.warn("redirectToSafePath: 無効なリダイレクトURLです", {
        redirectUrl: sanitizedRedirectUrl,
        error,
      });
    }
  }

  window.location.assign(safeFallbackPath);
  return true;
}

const REDIRECT_TOAST_SESSION_KEY = "redirect-toast";
const MAX_REDIRECT_TOAST_MESSAGE_LENGTH = 300;
const MAX_REDIRECT_TOAST_ID_LENGTH = 100;
const ALLOWED_REDIRECT_TOAST_TYPES = new Set([
  "error",
  "success",
  "info",
  "warn",
  "warning",
]);

function normalizeRedirectToast(toastMessage) {
  if (!toastMessage) return null;

  const defaultToast = { message: "", type: "error", toastId: "session-expired" };
  const rawToast =
    typeof toastMessage === "string"
      ? { ...defaultToast, message: toastMessage }
      : { ...defaultToast, ...toastMessage };

  const rawMessage =
    typeof rawToast.message === "string"
      ? rawToast.message
      : rawToast.message == null
        ? ""
        : String(rawToast.message);
  const normalizedMessage = rawMessage.trim().slice(0, MAX_REDIRECT_TOAST_MESSAGE_LENGTH);
  if (!normalizedMessage) return null;

  const normalizedType = String(rawToast.type || "error").toLowerCase();
  const safeType = ALLOWED_REDIRECT_TOAST_TYPES.has(normalizedType)
    ? normalizedType
    : "error";

  const rawToastId = String(rawToast.toastId || "session-expired").trim();
  const safeToastId =
    rawToastId.slice(0, MAX_REDIRECT_TOAST_ID_LENGTH) || "session-expired";

  return {
    message: normalizedMessage,
    type: safeType,
    toastId: safeToastId,
  };
}

function setRedirectToast(toastMessage) {
  const normalizedToast = normalizeRedirectToast(toastMessage);
  if (!normalizedToast?.message) return;

  try {
    sessionStorage.setItem(
      REDIRECT_TOAST_SESSION_KEY,
      JSON.stringify(normalizedToast),
    );
  } catch (_error) {
    // ストレージが利用できない場合は無視する
  }
}

export function consumeRedirectToast() {
  try {
    const storedValue = sessionStorage.getItem(REDIRECT_TOAST_SESSION_KEY);
    if (!storedValue) return null;

    try {
      const parsedToast = JSON.parse(storedValue);
      sessionStorage.removeItem(REDIRECT_TOAST_SESSION_KEY);
      return parsedToast;
    } catch (_parseError) {
      sessionStorage.removeItem(REDIRECT_TOAST_SESSION_KEY);
      return {
        message: "セッションの有効期限が切れました。再度ログインしてください。",
        type: "error",
        toastId: "session-expired",
      };
    }
  } catch (_error) {
    return null;
  }
}

export function hasPendingRedirectToast() {
  try {
    return !!sessionStorage.getItem(REDIRECT_TOAST_SESSION_KEY);
  } catch (_error) {
    return false;
  }
}

export function handleUnauthorizedOrRedirect(response, options = {}) {
  const {
    fallbackPath = "/users/sign_in",
    onUnauthorized,
    redirectDelayMilliseconds = 0,
    preserveUnauthorizedToastMessage,
    preferFallbackPathOnRedirect = false,
    showUnauthorizedToastBeforeRedirect = false,
  } = options;
  const safeFallbackPath = normalizeToSafeSameOriginPath(fallbackPath);
  const shouldPersistUnauthorizedToastForRedirect =
    !!preserveUnauthorizedToastMessage && !showUnauthorizedToastBeforeRedirect;

  if (response.status === 401) {
    if (shouldPersistUnauthorizedToastForRedirect) {
      setRedirectToast(preserveUnauthorizedToastMessage);
    }
    const shouldShowUnauthorizedToastNow =
      !preserveUnauthorizedToastMessage || showUnauthorizedToastBeforeRedirect;
    if (shouldShowUnauthorizedToastNow) {
      onUnauthorized?.();
    }

    if (safeFallbackPath) {
      setTimeout(() => {
        window.location.assign(safeFallbackPath);
      }, redirectDelayMilliseconds);
    }

    return true;
  }

  if (response.redirected) {
    if (shouldPersistUnauthorizedToastForRedirect) {
      setRedirectToast(preserveUnauthorizedToastMessage);
    }

    if (preferFallbackPathOnRedirect && safeFallbackPath) {
      window.location.assign(safeFallbackPath);
      return true;
    }

    redirectToSafePath(response.url, safeFallbackPath);
    return true;
  }

  return false;
}
